/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useContext, useEffect, useState } from "react";
import {
  Routes,
  Route,
  Outlet,
  useLocation,
  Navigate,
} from "react-router-dom";
import { GlobalContext } from "../context/GlobalContext";
import { useUploadActions } from "../context/UploadContext";
import DefaultLayout from "../layout/DefaultLayout";
// todo: Resolve path for alias
import Login from "../pages/LoginPage";
// import LoginWithGoogle from "../pages/LoginWithGooglePage";
import CalendarCallback from "../components/Integrations/CalendarCallback";
import Meetings from "../pages/MeetingsPage";
import Analytics from "../pages/AnalyticsPage";
import Insights from "../pages/InsightsPage";
import Target from "../pages/TargetsPage";
import Chat from "../pages/ChatPage";
import PageNotFound from "../components/PageNotFound";
import ProgressSpinner from "../components/Common/ProgressSpinner";
import useGlobalContext from "../context/useGlobalContext";
import useDisplaySnackbar from "../utils/useDisplaySnackbar";
import ClientMeetingActionView from "../components/Client/ClientMeetingActionView";
import GlobalSearchMeetingsPage from "../components/GlobalSearch/GlobalSearchMeetingsPage";
import GlobalSearchActionPage from "../components/GlobalSearch/GlobalSearchActionPage";
import GlobalSearchClientPage from "../components/GlobalSearch/GlobalSearchClientPage";
import GlobalSearchPeoplePage from "../components/GlobalSearch/GlobalSearchPeoplePage";
import AdminPage from "../components/AdminPage/AdminPage";
import { clearCurrentSession, getUserAttributes, setCurrentSession } from "../utils/auth";
import CommonService from "../services/common/commonService";
import Reporting from "../components/Reporting/Reporting";
import CalendarService from "../services/calender/CalendarService";
import MeetingDetailsPage from "../components/MeetingDetailsPage/MeetingDetailsPage";
import { normalizeFeatureFlags } from "../utils/utils";
import Ask7G from "../components/Reporting/Ask7G";
import ClientOverviewPage from "../components/ClientOverview/ClientOverviewPage";
import TaskManagementPage from "../pages/TaskManagementPage";
import UnauthorizedPage from "../components/Common/UnauthorizedPage";
import SettingsPage from "../pages/SettingsPage";
import { buildImpersonationFeatureMap, DEFAULT_FEATURE_MAP } from "../utils/types";
import ProtectedRoute from "../components/Common/ProtectedRoute";
import AccountPage from "../pages/AccountPage";
import OpportunitiesPage from "../pages/OpportunitiesPage";
import ContactsPage from "../pages/ContactsPage";

const PrivateRoutes = () => { 
  const location = useLocation();
  const [fetchCalendars, setFetchCalendars] = useState(false);
  const {
    setCalendarsData,
    setCalendar,
    setImpersonationReady,
    spaceList,
    setSpaceList,
    setSelectedView,
    setSelectedSpace,
    impersonationReady,
    featureMap
  } = useGlobalContext();
  const [authId, setAuthId] = useState<string | null>(null);
  const [loggedInEmail, setLoggedInEmail] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const { userExists, setUserExists, setFeatureStatus, setFeatureMap } = useContext(GlobalContext);
  const { showSnackbar } = useDisplaySnackbar();
  const { setRoleId, impersonationDetails } = useContext(GlobalContext);
  const { setAdminScreen, setAccountsReports } = useContext(GlobalContext);
  const { setUserTeamData, setAccountDetails, accountDetails } = useContext(GlobalContext);
  const depth_level = 1;
  const [isUnauthorizedUser, setIsUnauthorizedUser] = useState(false);
  const loggedInProfileOrgId = accountDetails?.profileOrgId;
  const isImpersonating = impersonationDetails?.isImpersonating;
  const impersonatingUserPorId = impersonationDetails?.impersonatingUserPorId;
  const accessLevel = impersonationDetails?.accessType;
  // Account details are now handled in auth.ts, no need for separate API call

  const {
    isLoading: isNewFeatureFlagLoading,
    isFetching: isNewFeatureFlagFetching,
    isSuccess: isNewFeatureFlagSuccess,
    isError: isNewFeatureFlagError,
    error: NewFeatureFlagError,
    data: newFeatureFlagData,
  } = CommonService.useGetNewFeatureFlags(isAuthenticated && userExists);

  const {
    isLoading: isCalendarsLoading,
    isFetching: isCalendarsFetching,
    isSuccess: isCalendarsSuccess,
    isError: isCalendarsError,
    error: Calendarserror,
    data: calendarsData,
  } = CalendarService.useGetCalendarDetails({
    employeePorId: loggedInProfileOrgId || "",
  }, fetchCalendars && isAuthenticated && userExists && !!loggedInProfileOrgId);

  const activeEmployeePorId =
    impersonationDetails?.accessType === "team"
      ? impersonationDetails.impersonatingUserPorId
      : loggedInProfileOrgId;

  const {
    isLoading,
    isSuccess,
    isError,
    error,
    data: userHierarchyData,
  } = CommonService.useGetHierarchyInfo({
    employeePorId : activeEmployeePorId || "",
    depthLevel : depth_level
  }, userExists && !!activeEmployeePorId);

  const {
    isSuccess: isUserGroupsSuccess,
    data: userGroupsData,
  } = CommonService.useGetUserGroups(
    {
      impersonatingPorId: impersonatingUserPorId,
      accessLevel: accessLevel
    },
    isAuthenticated && userExists && (!isImpersonating || impersonationReady)
  );

  useEffect(() => {
    if (isUserGroupsSuccess && userGroupsData) {
      const spaces = Object.entries(userGroupsData).map(([label, value]) => ({
        label,
        value: value as string,
      }));

      const currentSpaceIds = spaces.map((s) => s.value);
      const savedView = sessionStorage.getItem("selectedView") as "org" | "spaces" | null;
      const savedSpace = sessionStorage.getItem("selectedSpace");

      // Check if spaces actually changed to prevent unnecessary updates or rendering loops
      const spacesChanged =
        spaces?.length !== spaceList?.length ||
        spaces.some((s, i) => spaceList[i]?.value !== s.value);

      if (spacesChanged) {
        setSpaceList(spaces);
      }

      const shareWithSpaceEnabled = featureMap?.share_with_space === true;

      if (spaces?.length > 0 && shareWithSpaceEnabled) {
        if (savedView) {
          setSelectedView(savedView);
          let updatedSelection: string[] = [];
          if (savedSpace) {
            try {
              const parsedSaved = JSON.parse(savedSpace) as string[];
              updatedSelection = parsedSaved.filter((id) => currentSpaceIds.includes(id));
            } catch (e) {
              updatedSelection = currentSpaceIds;
            }
          }

          if (updatedSelection?.length === 0) {
            updatedSelection = currentSpaceIds;
          }

          // If this is a dynamic update and spaceList already has elements
          if (spaceList?.length > 0) {
            const newSpaceIds = currentSpaceIds.filter(
              (id) => !spaceList.some((old) => old.value === id)
            );
            if (newSpaceIds.length > 0) {
              updatedSelection = Array.from(new Set([...updatedSelection, ...newSpaceIds]));
            }
          }
          setSelectedSpace(updatedSelection);
        } else {
          setSelectedView("spaces");
          setSelectedSpace(currentSpaceIds);
        }
      } else {
        setSelectedView("org");
        setSelectedSpace([]);
      }
    }
  }, [isUserGroupsSuccess, userGroupsData, spaceList, setSpaceList, setSelectedView, setSelectedSpace, featureMap]);

  useEffect(() => {
    if (isNewFeatureFlagLoading || isNewFeatureFlagFetching) {
      setFeatureStatus("loading");
      return;
    }

    if (isNewFeatureFlagSuccess) {
      const featureFlags = newFeatureFlagData?.data || [];
      const normalized = isImpersonating
        ? buildImpersonationFeatureMap(featureFlags)
        : normalizeFeatureFlags(featureFlags);
      setFeatureMap(normalized);
      setFeatureStatus("ready");
      if (isImpersonating) {
        setImpersonationReady(true);
      }
      return;
    }
  
    if (isNewFeatureFlagError) {
      console.error("Feature flag API failed", NewFeatureFlagError);
      setFeatureMap(isImpersonating ? buildImpersonationFeatureMap([]) : DEFAULT_FEATURE_MAP);
      setFeatureStatus("error");
    }
  }, [
    isImpersonating,
    isNewFeatureFlagLoading,
    isNewFeatureFlagFetching,
    isNewFeatureFlagSuccess,
    isNewFeatureFlagError,
    newFeatureFlagData,
    setFeatureMap,
    setFeatureStatus,
  ]);
  
  useEffect(() => {
    if (isSuccess) {
      const result = userHierarchyData?.data;

      if (typeof result === "string") {
        setUserTeamData({
          userTeamDataMessage: "",
          showTeamTab: false,
        });// Show the team tab only if the message isn't a no-team message
      } else if (Array.isArray(result) && result.length === 0) {
        // Empty array = no team
        setUserTeamData({
          userTeamDataMessage: "",
          showTeamTab: false,
        });
      } else {
        setUserTeamData({
          userTeamDataMessage: "",
          showTeamTab: true,
        }); // Show the team tab when there's valid team data
      }
    } else if (isError) {
      showSnackbar("error", error);
      setUserTeamData({
        userTeamDataMessage: "",
        showTeamTab: false,
      });
    }
  }, [isLoading, isSuccess, isError]);


  const validateUserSession = async (): Promise<{ id: string | null; email: string | null }> => {
  try {
    const { accountDetails } = await setCurrentSession(); // or fetchCurrentSession
    setAccountDetails(accountDetails);
    const attrs = getUserAttributes();
    if (!attrs?.id) {
      throw new Error("EMPTY_SESSION");
    }
    // success
    setAuthId(attrs.id);
    setLoggedInEmail(attrs.email ?? null);
    setIsAuthenticated(true);
    return { id: attrs.id, email: attrs.email ?? null };
  } catch (err) {
    setIsAuthenticated(false);
    // Check if the error is due to unauthorized access (403)
    if (err instanceof Error && err.message.includes('403')) {
      setIsUnauthorizedUser(true);
    }
    throw err;
  }
};


  const checkUserStatus = async () => {
    // Check if user has valid session by calling our auth API
    try {
      await validateUserSession();
      if (!userExists && !isUnauthorizedUser) {
        localStorage.removeItem("isSignedOutOrTokenExpired");
        // Since account details are now in localStorage, we can set userExists directly
        if (accountDetails) {
          setUserExists(true);
          setFetchCalendars(true);
        }
      }
    } catch (err) {
      console.error("Authentication failed:", err);
      // Only handle logout if it's not a 403 (unauthorized) error being handled elsewhere
      if (!isUnauthorizedUser) {
        handleLogout();
      }
    }
  };

  const { abortAllUploads } = useUploadActions();
  const handleLogout = async () => {
    try {
      await abortAllUploads();
    } catch {
      // ignore failure
    }
    const routeToRedirect = `${location.pathname}${location.search}${location.hash}`;
    setUserExists(false);
    setIsAuthenticated(false);
    const isSignedOutOrTokenExpired = JSON.parse(localStorage.getItem("isSignedOutOrTokenExpired") || "false");
    localStorage.removeItem("UserInfo");
    clearCurrentSession();
    localStorage.removeItem("isPresentCalendar");
    localStorage.removeItem("TaskPreferences");
    sessionStorage.removeItem("meetings_date_range_filter");
    sessionStorage.removeItem("TaskManagementDueDateRange");
    sessionStorage.removeItem("TaskManagementTableState");
    sessionStorage.removeItem("impersonationDetails");
    sessionStorage.removeItem("customerColumnVisibility");
    sessionStorage.removeItem("customerFilters");
    sessionStorage.removeItem("customerActiveViewName");
    sessionStorage.removeItem("selectedView");
    sessionStorage.removeItem("selectedSpace");
    sessionStorage.removeItem("multipart-upload");
    sessionStorage.removeItem("mmna_pipeline_columnOrder");
    
    // setTimeout hack is introduced to ensure all data from cookies and local Storage clears before we navigate to website
    setTimeout(() => {
      if (!isSignedOutOrTokenExpired) {
        window.location.href = '/login';
      } else {
        let redirectUrl = import.meta.env.VITE_APP_LOGOUT;
        if (routeToRedirect) {
          redirectUrl += `?continue=${routeToRedirect}`;
        }
        window.location.href = redirectUrl;
      }
    }, 1000);
  };

  useEffect(() => {
    checkUserStatus();
  }, []); // Run once on component mount

  useEffect(() => {
    if (isAuthenticated && authId && loggedInEmail) {
      setFetchCalendars(true);
    }
  }, [isAuthenticated, authId, loggedInEmail]);

  useEffect(() => {
    // When authentication is successful, set user data from localStorage
    if (isAuthenticated && authId && loggedInEmail) {
      setIsUnauthorizedUser(false);
      if (accountDetails) {
        setRoleId(accountDetails?.roleId);
        setAdminScreen(false);
        setAccountsReports(false);
        setUserExists(true);
        setFetchCalendars(true);
      } else {
        console.error("Account details missing, signing out...");
      }
    }
  }, [isAuthenticated, authId, loggedInEmail]);

  useEffect(() => {
    if (isCalendarsSuccess) {
      setCalendarsData(calendarsData);
      setCalendar(calendarsData);
    }
    if (isCalendarsError) {
      console.error("Error fetching calendar details:", Calendarserror);
      showSnackbar("error", Calendarserror);
    }
  }, [
    isCalendarsLoading,
    isCalendarsFetching,
    isCalendarsSuccess,
    isCalendarsError,
  ]);

  if (isAuthenticated) {
    if(isUnauthorizedUser) {
      return <Navigate to="/unauthorized" replace />;
    }
    if (isNewFeatureFlagLoading || isNewFeatureFlagFetching) {
      return <ProgressSpinner showSpinner={true} />;
    }
    if (userExists) {
      return (
        <DefaultLayout>
          <Outlet />
        </DefaultLayout>
      );
    } else return <ProgressSpinner showSpinner={true} />;
  }

  return null;
};

const AppRouter: React.FC = () => {
  const { featureMap, accountDetails } = useGlobalContext();
  const isAskSevenGFlagEnalbled = featureMap?.ask_7g_page === true;
  const isExternalUser = accountDetails?.isExternalUser ?? false;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      {/* <Route path="/google" element={<LoginWithGoogle />} /> */}
      <Route path="/unauthorized" element={<UnauthorizedPage />} />
      <Route element={<PrivateRoutes />}>
      <Route
          path="/"
          element={
            <ProtectedRoute feature="new_client_overview_page">
              <ClientOverviewPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/home"
          element={ isExternalUser ? (
              <Meetings />
            ) : (
              <ProtectedRoute feature="new_client_overview_page">
                <ClientOverviewPage />
              </ProtectedRoute>
            ) 
          }
        />
        <Route 
          path="/meetings/my" 
          element={
            <ProtectedRoute feature="my_interaction">
              <Meetings />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/meetings/team" 
          element={
            <ProtectedRoute feature="team_interactions">
              <Meetings />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/meetings/shared-with-me" 
          element={
            <ProtectedRoute feature="shared_with_me">
              <Meetings />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/meetings/shared-by-me" 
          element={
            <ProtectedRoute feature="shared_by_me">
              <Meetings />
            </ProtectedRoute>
          } 
        />
        <Route path="/meeting/:id" element={ <MeetingDetailsPage/> } />

        {/* <Route path="/meeting/:id" element={<DemoMeeting />} /> */}
        <Route
          path="/tasks/all-tasks"
          element={
            <ProtectedRoute feature="all_tasks">
              <TaskManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tasks/my-tasks"
          element={
            <ProtectedRoute feature="my_tasks">
              <TaskManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tasks/team-tasks"
          element={
            <ProtectedRoute feature="team_tasks">
              <TaskManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tasks/subscribed-tasks"
          element={
            <ProtectedRoute feature="subscribed_tasks">
              <TaskManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tasks/complete-mom-tasks"
          element={
            <ProtectedRoute feature="complete_mom_tasks">
              <TaskManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tasks/ai-recommended-tasks"
          element={
            <ProtectedRoute feature="ai_recommended_tasks">
              <TaskManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client"
          element={
            <ProtectedRoute feature="new_client_overview_page">
              {<ClientOverviewPage />}
            </ProtectedRoute>
          }
        />
      <Route path="/client/:client_domain" element={<ClientMeetingActionView />} />
        {/*Global search Paths */}
        <Route
          path="/peoplemeetings/:profile_org_id"
          element={
            <GlobalSearchPeoplePage />
          }
        />
        <Route
          path="/peoplemeetings/:profile_org_id/:tab"
          element={
            <GlobalSearchPeoplePage />
          }
        />
        <Route
          path="/clients/search"
          element={
            <GlobalSearchClientPage />
          }
        />
        <Route
          path="/meetings/search"
          element={
            <GlobalSearchMeetingsPage />
          }
        />
        <Route
          path="/action/search"
          element={
            <GlobalSearchActionPage />
          }
        />

        <Route
          path="/analytics/interactions"
          element={
            <ProtectedRoute feature="interaction_analytics">
              {<Analytics />}
            </ProtectedRoute>
          }
        />
        <Route
          path="/analytics/tasks"
          element={
            <ProtectedRoute feature="task_analytics">
              {<Analytics />}
            </ProtectedRoute>
          }
        />
        <Route
          path="/analytics/business"
          element={
            <ProtectedRoute feature="bussiness_analytics">
              {<Analytics />}
            </ProtectedRoute>
          }/>
        <Route
          path="/analytics/report"
          element={
            <ProtectedRoute feature="report_analytics">
              {<Analytics />}
            </ProtectedRoute>
          }
        />
        <Route
          path="/api/v1/calendar/callback"
          element={<CalendarCallback />}
        />
        <Route path="/insights" element={<Insights />} />
        <Route path="/targets" element={<Target />} />
        <Route path="/chats" element={<Chat />} />
       <Route 
          path="/settings/user-settings" 
          element={
            <ProtectedRoute feature="user_settings">
              <SettingsPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/settings/space-settings" 
          element={
            <ProtectedRoute feature="space_settings">
              <SettingsPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/settings/notification-settings" 
          element={
            <ProtectedRoute feature="notification_settings">
              <SettingsPage />
            </ProtectedRoute>
          } 
        />
        <Route path="/settings/admin" element={<AdminPage/>} />
        <Route 
          path="/settings/app-config"
          element={
            <ProtectedRoute feature="app_settings">
              <SettingsPage />
            </ProtectedRoute>
          } 
        />
        <Route
          path="/settings/org-management"
          element={
            <ProtectedRoute feature="org_management">
              <SettingsPage/>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/video-guides"
          element={
            <ProtectedRoute feature="demo_video">
              <SettingsPage/>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reporting"
          element={
            <ProtectedRoute feature="reporting_page">
                {isAskSevenGFlagEnalbled ? <Ask7G /> : <Reporting />}
            </ProtectedRoute>
          }
        />
        <Route
          path="/contacts"
          element={
             <ProtectedRoute feature="contacts_page">
              <ContactsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/accounts/vendor"
          element={
            <ProtectedRoute feature="vendors">
              {<AccountPage />}
            </ProtectedRoute>
          }
        />
        <Route
          path="/accounts/prospect"
          element={
            <ProtectedRoute feature="prospects">
              {<AccountPage />}
            </ProtectedRoute>
          }
        />
        <Route
          path="/accounts/customer"
          element={
            <ProtectedRoute feature="customers">
              {<AccountPage />}
            </ProtectedRoute>
          }
        />
        <Route
          path="/accounts/spaces"
          element={
            <ProtectedRoute feature="spaces">
              {<AccountPage />}
            </ProtectedRoute>
          }
        />
        <Route
          path="/opportunities"
          element={
            <ProtectedRoute feature="opportunities_page">
              {<OpportunitiesPage/>}
            </ProtectedRoute>
          }
        />
        {/* <Route path="/analytics/:userName" element={<UserAnalytics/>} /> */}
      </Route>
      <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
};

export default AppRouter;
